// Admin-POI-Modul — Server-cached Points of Interest, von Admins markiert.
// ----------------------------------------------------------------------
//
// Wave 14cf-fix9: parallel zu drinking_water (lib/poi.ts) und userPois
// (lib/userPois.ts):
//
//   - drinking_water → OSM-gepulled vom Backend, alle User sehen die.
//   - admin_pois     → vom Admin direkt markiert, alle User sehen die. (HIER)
//   - user_pois      → vom User markiert, nur dieser User sieht sie.
//
// API:
//   loadAdminPoisWithRefresh()    Stale-while-revalidate fetch + Cache.
//   postAdminPoi(input)           Server-Push (mit offline-Queue).
//   listQueuedAdminPois()         Pending in der Offline-Queue.
//   flushAdminPoiQueue()          Versucht alle pending zu pushen.
//
// Persistenz: ein Single-Row-Blob in IDB (adminPoiBlob, key='all'). Klein
// genug — selbst 5000 Admin-POIs = ~500 KB JSON, problemlos für IDB. Für
// die Render-Side (Map-Layer) holen wir uns immer den ganzen Blob und
// filtern client-side per Kategorie.

import { withDb } from './storage/db';

// ─── Types ──────────────────────────────────────────────────────────────

export interface AdminPoi {
  id: string;
  category: string;
  lat: number;
  lon: number;
  ele?: number | null;
  note?: string | null;
  /** Wave 14cf-fix15: Anzeigename. Wird vom User-Detail-Sheet groesser
   *  als note gerendert wenn vorhanden. */
  name?: string | null;
  /** Wave 14cf-fix15: Oeffnungszeiten als Freitext (nicht-water Kategorien). */
  openingHours?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
}

interface BackendRow {
  id: string;
  category: string;
  lat: number;
  lon: number;
  ele: number | null;
  note: string | null;
  name: string | null;
  opening_hours: string | null;
  created_at: string;
  created_by_user_id: string | null;
}

interface BackendListResponse {
  pois: BackendRow[];
  count: number;
}

// Stale-Threshold: nach 1h re-fetchen wenn online. Admin-POIs ändern sich
// selten — länger ist OK, kürzer wäre aber auch unproblematisch (~kB).
const CACHE_MAX_AGE_MS = 60 * 60 * 1000;

// Net-Timeout für Backend-Calls. 10s ist großzügig für mobiles Netz; im
// schlechten Netz fallen wir lieber auf den Cache zurück als die UI zu
// blockieren.
const NET_TIMEOUT_MS = 10_000;

// ─── Backend-Client ─────────────────────────────────────────────────────

function rowToPoi(row: BackendRow): AdminPoi {
  return {
    id: row.id,
    category: row.category,
    lat: row.lat,
    lon: row.lon,
    ele: row.ele,
    note: row.note,
    name: row.name ?? null,
    openingHours: row.opening_hours ?? null,
    createdAt: row.created_at,
    createdByUserId: row.created_by_user_id,
  };
}

async function fetchAdminPoisFromBackend(): Promise<AdminPoi[]> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), NET_TIMEOUT_MS);
  try {
    const url = `/api/pois/admin?_=${Date.now()}`;
    const res = await fetch(url, {
      signal: ctrl.signal,
      cache: 'reload',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache',
      },
    });
    if (!res.ok) {
      throw new Error(`Server returned HTTP ${res.status}`);
    }
    const data = (await res.json()) as BackendListResponse;
    if (!Array.isArray(data?.pois)) {
      throw new Error('Server returned invalid response');
    }
    return data.pois.map(rowToPoi);
  } finally {
    clearTimeout(t);
  }
}

// ─── Cache ──────────────────────────────────────────────────────────────

interface CachedBlob {
  key: string;
  cachedAt: string;
  pois: AdminPoi[];
}

async function loadCache(): Promise<CachedBlob | null> {
  return withDb(async (db) => {
    const row = (await db.get('adminPoiBlob', 'all')) as CachedBlob | undefined;
    return row ?? null;
  });
}

async function storeCache(pois: AdminPoi[]): Promise<void> {
  await withDb(async (db) => {
    await db.put('adminPoiBlob', {
      key: 'all',
      cachedAt: new Date().toISOString(),
      pois,
    });
  });
}

/**
 * Cached + stale-while-revalidate fetch. Verhalten:
 *   - Cache vorhanden + frisch (<1h):  sofort zurück, kein Netz.
 *   - Cache vorhanden + stale:         Cache zurück, im Hintergrund refresh.
 *   - Cache leer:                      blockierender Netz-Fetch.
 *   - Offline + leerer Cache:          [], error.
 */
export async function loadAdminPoisWithRefresh(): Promise<{
  pois: AdminPoi[];
  fromCache: boolean;
  error?: string;
}> {
  const cached = await loadCache();
  const fresh =
    cached != null &&
    Date.now() - new Date(cached.cachedAt).getTime() < CACHE_MAX_AGE_MS;

  if (cached && fresh) {
    return { pois: cached.pois, fromCache: true };
  }
  if (cached && !fresh) {
    refreshInBackground();
    return { pois: cached.pois, fromCache: true };
  }
  // Cache leer → blocking fetch.
  try {
    const pois = await fetchAdminPoisFromBackend();
    await storeCache(pois);
    return { pois, fromCache: false };
  } catch (err) {
    return {
      pois: [],
      fromCache: false,
      error: err instanceof Error ? err.message : String(err),
    };
  }
}

let refreshInFlight = false;
function refreshInBackground(): void {
  if (refreshInFlight) return;
  refreshInFlight = true;
  void (async () => {
    try {
      const pois = await fetchAdminPoisFromBackend();
      await storeCache(pois);
    } catch {
      /* silent — stale Cache bleibt */
    } finally {
      refreshInFlight = false;
    }
  })();
}

// ─── POST + Offline-Queue ───────────────────────────────────────────────

export interface PostAdminPoiInput {
  category: string;
  lat: number;
  lon: number;
  ele?: number | null;
  note?: string | null;
}

/**
 * Push an Admin-POI zum Server. Wenn das fehlschlägt (offline, Server
 * down, 5xx), parken wir die Payload in adminPoiQueue und probieren's
 * beim nächsten flushAdminPoiQueue() erneut. UI bekommt sofort eine
 * synthetic AdminPoi-Antwort zurück damit der Layer instant updated.
 */
export async function postAdminPoi(
  input: PostAdminPoiInput,
): Promise<AdminPoi> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), NET_TIMEOUT_MS);
  let backendResp: BackendRow | null = null;
  let backendErr: unknown = null;
  try {
    const res = await fetch('/api/pois/admin', {
      method: 'POST',
      signal: ctrl.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(input),
    });
    if (res.ok) {
      backendResp = (await res.json()) as BackendRow;
    } else {
      backendErr = new Error(`Server returned HTTP ${res.status}`);
    }
  } catch (err) {
    backendErr = err;
  } finally {
    clearTimeout(t);
  }

  if (backendResp) {
    // Cache lokal mit dem neuen Eintrag aktualisieren — sonst wäre er
    // bis zum nächsten refreshInBackground unsichtbar.
    const poi = rowToPoi(backendResp);
    await mergeIntoCache(poi);
    return poi;
  }

  // Push fehlgeschlagen — in Queue parken, sofort eine optimistische
  // Local-AdminPoi zurückgeben damit das UI den Marker auf der Map
  // sehen kann.
  const id = `apoi_local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const optimistic: AdminPoi = {
    id,
    category: input.category,
    lat: input.lat,
    lon: input.lon,
    ele: input.ele ?? null,
    note: input.note?.trim() ? input.note.trim() : null,
    createdAt: new Date().toISOString(),
    createdByUserId: null,
  };
  await withDb(async (db) => {
    await db.put('adminPoiQueue', {
      id,
      category: optimistic.category,
      lat: optimistic.lat,
      lon: optimistic.lon,
      ele: optimistic.ele ?? null,
      note: optimistic.note ?? null,
      queuedAt: optimistic.createdAt,
      attempts: 0,
      lastError: backendErr instanceof Error ? backendErr.message : String(backendErr),
    });
  });
  await mergeIntoCache(optimistic);
  return optimistic;
}

async function mergeIntoCache(poi: AdminPoi): Promise<void> {
  await withDb(async (db) => {
    const cached = (await db.get('adminPoiBlob', 'all')) as
      | CachedBlob
      | undefined;
    const next: CachedBlob = {
      key: 'all',
      cachedAt: new Date().toISOString(),
      // Falls schon eine ID-gleiche Entry existiert, ersetzen.
      pois: [...((cached?.pois ?? []).filter((p) => p.id !== poi.id)), poi],
    };
    await db.put('adminPoiBlob', next);
  });
}

/**
 * Flush der Offline-Queue. Versucht jeden geparkten POI an den Server
 * zu pushen. Bei Erfolg wird der Queue-Eintrag entfernt UND der Local-
 * Cache mit dem Server-zurückgegebenen Eintrag (mit echter Server-ID)
 * gemerged; den optimistischen Local-ID-Eintrag im Cache räumen wir weg.
 */
export async function flushAdminPoiQueue(): Promise<{
  pushed: number;
  failed: number;
}> {
  let pushed = 0;
  let failed = 0;
  const queued = await withDb(async (db) => {
    return (await db.getAll('adminPoiQueue')) as Array<{
      id: string;
      category: string;
      lat: number;
      lon: number;
      ele?: number | null;
      note?: string | null;
      queuedAt: string;
      attempts: number;
      lastError?: string;
    }>;
  });
  for (const q of queued) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), NET_TIMEOUT_MS);
      const res = await fetch('/api/pois/admin', {
        method: 'POST',
        signal: ctrl.signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          category: q.category,
          lat: q.lat,
          lon: q.lon,
          ele: q.ele,
          note: q.note,
        }),
      });
      clearTimeout(t);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const row = (await res.json()) as BackendRow;
      const serverPoi = rowToPoi(row);
      // Queue-Eintrag löschen + Cache: alten Local-ID-Eintrag durch
      // Server-ID-Eintrag ersetzen.
      await withDb(async (db) => {
        await db.delete('adminPoiQueue', q.id);
        const cached = (await db.get('adminPoiBlob', 'all')) as
          | CachedBlob
          | undefined;
        const filtered = (cached?.pois ?? []).filter((p) => p.id !== q.id);
        const next: CachedBlob = {
          key: 'all',
          cachedAt: new Date().toISOString(),
          pois: [...filtered, serverPoi],
        };
        await db.put('adminPoiBlob', next);
      });
      pushed++;
    } catch (err) {
      failed++;
      // Attempt count incrementen, lastError speichern. Wir lassen den
      // Eintrag in der Queue für den nächsten Flush.
      await withDb(async (db) => {
        const row = (await db.get('adminPoiQueue', q.id)) as typeof q | undefined;
        if (row) {
          await db.put('adminPoiQueue', {
            ...row,
            attempts: (row.attempts ?? 0) + 1,
            lastError: err instanceof Error ? err.message : String(err),
          });
        }
      });
    }
  }
  return { pushed, failed };
}

/** Helper für UI/Debug: wieviele POIs warten gerade in der Queue? */
export async function listQueuedAdminPois(): Promise<number> {
  const all = await withDb(async (db) => {
    return (await db.getAll('adminPoiQueue')) as unknown[];
  });
  return all.length;
}

/**
 * Wave 14cf-fix15: Cache invalidieren + Server-Fetch erzwingen.
 *
 * Verwendung: wenn der User das POI-Panel öffnet, gehen wir davon aus dass
 * er einen frisch angelegten POI sehen will — der 1h-Cache könnte das
 * verhindern. Diese Funktion löscht den Cache und holt synchron vom Server.
 *
 * Wenn das Netz fehlschlägt: alter Cache war eh schon weg, return [].
 * Der Caller muss damit rechnen.
 */
export async function forceRefreshAdminPois(): Promise<{
  pois: AdminPoi[];
  error?: string;
}> {
  try {
    const pois = await fetchAdminPoisFromBackend();
    await storeCache(pois);
    return { pois };
  } catch (err) {
    return {
      pois: [],
      error: err instanceof Error ? err.message : String(err),
    };
  }
}
